// T328 — the client re-scope engine.
//
// T327 put the per-application rows behind `?include=rows`; this is what makes
// them worth asking for. Everything on Analytics was served pre-aggregated, so
// the page could show "by source" and "by score band" and never "by source AND
// by score band" — the second question needed a round trip that did not exist,
// which is the shape of the operator's "cannot filter".
//
// Pure, and deliberately in its own module rather than inside the page: the
// parity test compares what this returns against the server's own rollups over
// the same rows, and a function buried in a 1,400-line component cannot be
// asked that question.

/** The dimensions a row can be sliced by, and where each reads from. */
export const DIMENSIONS = Object.freeze([
  { key: 'source', label: 'Source', of: (r) => r.source },
  { key: 'country', label: 'Country', of: (r) => r.country },
  { key: 'company', label: 'Company', of: (r) => r.company },
  { key: 'band', label: 'Score band', of: (r) => r.scoreBand },
  { key: 'outcome', label: 'Outcome', of: (r) => outcomeOf(r) },
]);

/**
 * One word for where an application ended up.
 *
 * Not `status`: the question the page asks is "what happened", and `applied`
 * covers both "sent last week" and "sent in May and never heard back". Those
 * are the same status and opposite outcomes, and conflating them is what the
 * ghosted count exists to undo.
 */
export function outcomeOf(row) {
  if (!row) return null;
  if (row.status === 'offer') return 'offer';
  if (row.status === 'rejected') return 'rejected';
  if (row.status === 'withdrawn') return 'withdrawn';
  // T350 — 'ghosted' wins over 'advanced' deliberately: a row at interview that
  // has been silent three weeks is a row that needs a nudge, and that is the
  // useful thing to be able to select.
  //
  // But then the chip could not be called "advanced", because the Advanced tile
  // beside it counts `r.advanced` — ever reached screening — and a stale
  // interview is in the tile and not in the chip. Two controls, same word, two
  // populations. The chip is now 'moving', which is what it actually selects:
  // advanced AND still live.
  if (row.ghosted) return 'ghosted';
  if (row.advanced) return 'moving';
  return 'waiting';
}

const asSet = (v) => (v instanceof Set ? v : new Set(v ? [].concat(v) : []));

/** `'2026-09-01'` → local 00:00:00.000 on that day, or null. */
function localDayStart(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  if (!m) return iso ? new Date(iso) : null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
}

/** `'2026-09-01'` → local 23:59:59.999 on that day, or null. */
function localDayEnd(iso) {
  const d = localDayStart(iso);
  if (!d) return null;
  d.setHours(23, 59, 59, 999);
  return d;
}

/** A value that no dimension can produce, so "unknown" is selectable. */
export const UNKNOWN = '—';

/**
 * Narrow `rows` by the selected dimension values and the date window.
 *
 * Every dimension is AND-ed with the others and OR-ed within itself, which is
 * what a chip group reads as: "jobindex or thehub, AND in Denmark".
 *
 * An empty set means "no filter on this dimension" — NOT "match nothing". That
 * distinction is the whole difference between a fresh page and an empty one,
 * and getting it backwards is a class of bug this codebase has hit before with
 * absent-vs-empty.
 */
export function applyFilters(rows = [], filters = {}) {
  // T350 — both bounds in the SAME frame, and that frame is the operator's.
  //
  // `new Date('2026-09-01')` is UTC midnight, but `setHours` moves a date in
  // LOCAL time. So `to` became the end of the local day while `from` stayed at
  // UTC midnight — two bounds in two frames. In Copenhagen that left a two-hour
  // band belonging to no day: an application at 2026-08-31T22:30Z is 00:30 on
  // the 1st locally, and was returned by neither `{from:'2026-09-01'}` nor
  // `{to:'2026-08-31'}`. West of UTC the same gap becomes an overlap, and a row
  // counts twice.
  //
  // A date typed into a date picker means a local day, so both ends are built
  // locally from its parts.
  const from = localDayStart(filters.from);
  const to = localDayEnd(filters.to);

  const selected = DIMENSIONS.map((d) => [d, asSet(filters[d.key])]).filter(([, s]) => s.size > 0);

  return rows.filter((row) => {
    if (from || to) {
      const at = row.appliedAt ? new Date(row.appliedAt) : null;
      if (!at || Number.isNaN(at.getTime())) return false;
      if (from && at < from) return false;
      if (to && at > to) return false;
    }
    for (const [d, want] of selected) {
      const v = d.of(row);
      if (!want.has(v == null || v === '' ? UNKNOWN : String(v))) return false;
    }
    return true;
  });
}

/**
 * The chip options for each dimension, with a count for each.
 *
 * Two rules, and T349 exists because the first was written down and the second
 * was not, so the code obeyed neither.
 *
 * 1. Each dimension is counted against every OTHER selected dimension, so the
 *    number beside a chip answers "if I also picked this, how many would I
 *    get". That is what makes the counts useful rather than decorative.
 *
 * 2. EVERY value the dimension takes anywhere in `rows` is listed, whatever
 *    rule 1 counts it at. The list is therefore stable: it never reflows as you
 *    click, and a zero tells you something ("thehub has nothing in Denmark")
 *    instead of leaving a hole.
 *
 *    Reproduced before this fix: rows [jobindex/DK, thehub/SE], select
 *    country=DK then source=thehub — the result is empty and BOTH selected
 *    chips disappear from the bar. "Showing 0 of 2", nothing on screen saying
 *    what is filtering, and no chip to toggle off; the only way out was the
 *    global Clear. A filter you cannot see is bad; a filter you cannot remove
 *    is a trap. The milder version bites too: filter by DK and the thehub chip
 *    vanishes, so you cannot widen back out along the axis you just narrowed.
 *
 * The old comment claimed the counts came from unfiltered rows, which would
 * have satisfied rule 2 by accident. They never did — the body counted against
 * the other filters and listed only what survived.
 */
export function facetsOf(rows = [], filters = {}) {
  return DIMENSIONS.map((d) => {
    const counts = new Map();
    const others = { ...filters, [d.key]: new Set() };
    for (const row of applyFilters(rows, others)) {
      const raw = d.of(row);
      const v = raw == null || raw === '' ? UNKNOWN : String(raw);
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    // Rule 2: seed every value the dimension takes across ALL rows, so the list
    // is the same length whatever is selected and only the numbers move.
    for (const row of rows) {
      const raw = d.of(row);
      const v = raw == null || raw === '' ? UNKNOWN : String(raw);
      if (!counts.has(v)) counts.set(v, 0);
    }
    // And a selected value survives even if it is no longer in `rows` at all —
    // a stale URL, or a row that left the window.
    for (const v of asSet(filters[d.key])) if (!counts.has(v)) counts.set(v, 0);

    const values = [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    return { key: d.key, label: d.label, values };
  });
}

/**
 * The rollups the cards read, recomputed over a filtered set.
 *
 * Only the ones a row can honestly answer. Anything needing `statusHistory`
 * timing — time-in-stage, cohorts, the apply-lag split — stays on the server's
 * all-time numbers and is labelled as such, because a row projection does not
 * carry the timestamps those need and inventing them here is how two numbers
 * for the same thing appear on one page.
 */
export function rollup(rows = []) {
  const applied = rows.length;
  const advanced = rows.filter((r) => r.advanced).length;
  // T350 — read the server's verdict, do not re-derive it. `repliedAt ||
  // rejected` looked equivalent to the server's `rung >= 1 || rejected` and is
  // not: a row at screening with no statusHistory has a rung and no timestamp,
  // so the funnel counted it replied and this line did not. Two numbers for one
  // thing, on one page.
  const replied = rows.filter((r) => r.replied).length;
  const ghosted = rows.filter((r) => r.ghosted).length;
  const offers = rows.filter((r) => r.status === 'offer').length;
  const pct = (n) => (applied > 0 ? Number(((n / applied) * 100).toFixed(1)) : null);
  return {
    applied,
    advanced,
    replied,
    ghosted,
    offers,
    advanceRatePct: pct(advanced),
    replyRatePct: pct(replied),
    ghostedRatePct: pct(ghosted),
    offerRatePct: pct(offers),
  };
}

/** True when anything is actually narrowing the set. */
export function isDirty(filters = {}) {
  if (filters.from || filters.to) return true;
  return DIMENSIONS.some((d) => asSet(filters[d.key]).size > 0);
}
